import { io } from 'socket.io-client';
import { LIMITS } from './protocol.js';
import { ServerClock, computeSample } from './clock.js';
export const EMPTY_ROOM_STATE = {
    status: 'connecting',
    error: null,
    room: null,
    playback: null,
    participants: [],
    messages: [],
    you: null,
    clockReady: false,
    pingMs: null,
    playbackRevision: 0,
    reactions: [],
    strokes: [],
    pausedBy: null,
};
/** Сколько сообщений держим в памяти. */
const MAX_MESSAGES = 300;
/** Столько же, сколько держит сервер: списки не должны расходиться. */
const MAX_REACTIONS = LIMITS.reactionBufferSize;
const MAX_STROKES = LIMITS.strokeBufferSize;
export class RoomConnection {
    socket = null;
    clock = new ServerClock();
    state = EMPTY_ROOM_STATE;
    listeners = new Set();
    clockTimer = null;
    burstTimer = null;
    disposed = false;
    messageTimer = null;
    pendingMessages = [];
    lastPresenceAt = 0;
    commandSequence = 0;
    clockGeneration = 0;
    lastPresenceFlags = '';
    roomCode;
    admissionToken;
    nickname;
    clientId;
    hostToken;
    authToken;
    avatar;
    serverUrl;
    constructor(options) {
        this.roomCode = options.roomCode;
        this.admissionToken = options.admissionToken ?? null;
        this.nickname = options.nickname;
        this.clientId = options.clientId;
        this.hostToken = options.hostToken ?? null;
        this.authToken = options.authToken ?? null;
        this.avatar = options.avatar ?? null;
        this.serverUrl = options.serverUrl;
    }
    // ─────────────────────────── подписка ───────────────────────────
    subscribe = (listener) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };
    getSnapshot = () => this.state;
    setState(patch) {
        this.state = { ...this.state, ...patch };
        for (const listener of this.listeners)
            listener();
    }
    // ─────────────────────────── жизненный цикл ───────────────────────────
    connect() {
        if (this.socket)
            return;
        this.disposed = false;
        const options = {
            // WebSocket сразу: long-polling для синхронизации слишком медленный,
            // но оставляем его запасным для строгих корпоративных сетей.
            transports: ['websocket', 'polling'],
            reconnectionDelay: 500,
            reconnectionDelayMax: 5000,
            timeout: 10_000,
        };
        const socket = this.serverUrl
            ? io(this.serverUrl, options)
            : io(options);
        this.socket = socket;
        socket.on('connect', () => {
            this.setState({ status: 'joining', error: null });
            this.startClockSync();
            this.join();
        });
        socket.on('disconnect', (reason) => {
            if (this.disposed)
                return;
            // Отключение по инициативе сервера socket.io сам не восстанавливает.
            if (reason === 'io server disconnect')
                socket.connect();
            this.setState({ status: 'reconnecting', clockReady: false });
        });
        socket.on('connect_error', () => {
            if (this.disposed)
                return;
            this.setState({
                status: 'reconnecting',
                error: { code: 'network', message: 'Нет связи с сервером, пробуем ещё раз…' },
            });
        });
        socket.on('room:snapshot', (snapshot) => this.applySnapshot(snapshot));
        socket.on('room:participants', (participants) => {
            this.setState({ participants });
        });
        socket.on('room:control_mode', ({ controlMode }) => {
            const room = this.state.room;
            if (room)
                this.setState({ room: { ...room, controlMode } });
        });
        socket.on('playback:update', ({ playback, action, byClientId, byNickname }) => {
            if (playback.epoch === this.state.playback?.epoch && (playback.revision ?? 0) < (this.state.playback?.revision ?? 0))
                return;
            // Кто поставил паузу, нужно показать в плеере: «общая пауза от Лены»
            // читается иначе, чем безымянная остановка.
            const pausedBy = playback.status === 'paused'
                ? action === 'pause' && byClientId && byNickname
                    ? { clientId: byClientId, nickname: byNickname }
                    : this.state.pausedBy
                : null;
            this.setState({
                playback,
                pausedBy,
                playbackRevision: this.state.playbackRevision + 1,
            });
        });
        socket.on('source:changed', ({ source }) => {
            const room = this.state.room;
            if (room)
                this.setState({ room: { ...room, source } });
        });
        socket.on('chat:message', (message) => {
            this.pendingMessages.push(message);
            if (this.pendingMessages.length > MAX_MESSAGES)
                this.pendingMessages.shift();
            if (this.messageTimer)
                return;
            this.messageTimer = setTimeout(() => {
                this.messageTimer = null;
                const byId = new Map(this.state.messages.map(m => [m.id, m]));
                for (const m of this.pendingMessages)
                    byId.set(m.id, m);
                this.pendingMessages = [];
                this.setState({ messages: [...byId.values()].sort((a, b) => a.createdAt - b.createdAt).slice(-MAX_MESSAGES) });
            }, 80);
        });
        socket.on('room:renamed', ({ name }) => { if (this.state.room)
            this.setState({ room: { ...this.state.room, name } }); });
        socket.on('room:settings', (settings) => {
            const room = this.state.room;
            if (room)
                this.setState({ room: { ...room, settings } });
        });
        socket.on('reaction:added', (reaction) => {
            this.setState({ reactions: [...this.state.reactions, reaction].slice(-MAX_REACTIONS) });
        });
        socket.on('draw:stroke', (stroke) => {
            this.setState({ strokes: [...this.state.strokes, stroke].slice(-MAX_STROKES) });
        });
        socket.on('draw:undone', ({ strokeId }) => {
            this.setState({ strokes: this.state.strokes.filter((s) => s.id !== strokeId) });
        });
        socket.on('draw:cleared', () => {
            this.setState({ strokes: [] });
        });
        socket.on('room:error', ({ code, message }) => {
            this.setState({ error: { code, message } });
        });
    }
    join() {
        this.socket?.emit('room:join', {
            roomCode: this.roomCode,
            admissionToken: this.admissionToken,
            nickname: this.nickname,
            clientId: this.clientId,
            hostToken: this.hostToken,
            authToken: this.authToken,
            avatar: this.avatar,
        }, (result) => {
            if (result.ok) {
                this.applySnapshot(result.snapshot);
                this.setState({ status: 'joined', error: null });
            }
            else {
                this.setState({ status: 'error', error: { code: result.error, message: result.message } });
            }
        });
    }
    applySnapshot(snapshot) {
        this.setState({
            room: snapshot.room,
            playback: snapshot.playback,
            participants: snapshot.participants,
            messages: snapshot.messages.slice(-MAX_MESSAGES),
            you: snapshot.you,
            reactions: snapshot.reactions ?? [],
            strokes: snapshot.strokes ?? [],
            playbackRevision: this.state.playbackRevision + 1,
        });
    }
    dispose() {
        this.disposed = true;
        if (this.messageTimer)
            clearTimeout(this.messageTimer);
        this.messageTimer = null;
        this.pendingMessages = [];
        if (this.clockTimer)
            clearInterval(this.clockTimer);
        if (this.burstTimer)
            clearTimeout(this.burstTimer);
        this.clockTimer = null;
        this.burstTimer = null;
        this.socket?.removeAllListeners();
        this.socket?.disconnect();
        this.socket = null;
        this.listeners.clear();
    }
    // ─────────────────────────── часы ───────────────────────────
    /**
     * Сначала серия быстрых замеров, чтобы поправка появилась за секунду,
     * затем редкие — они ловят дрейф часов и переключения сети.
     */
    startClockSync() {
        const generation = ++this.clockGeneration;
        if (this.burstTimer)
            clearTimeout(this.burstTimer);
        this.clock.reset();
        this.setState({ clockReady: false });
        let burstLeft = 8;
        const measure = () => {
            const socket = this.socket;
            if (!socket?.connected)
                return;
            const t0 = Date.now();
            socket.emit('time:ping', { t0 }, (pong) => {
                if (generation !== this.clockGeneration || socket !== this.socket || !socket.connected)
                    return;
                const t2 = Date.now();
                const sample = computeSample(pong.t0 || t0, pong.t1, t2);
                // Замер с огромной задержкой (пробуждение приложения, скачок сети)
                // только испортил бы оценку.
                if (this.disposed || sample.rttMs < 0 || sample.rttMs > 3000 || !Number.isFinite(sample.offsetMs))
                    return;
                this.clock.addSample(sample);
                this.setState({ clockReady: this.clock.isReady(), pingMs: this.clock.getBestRttMs() });
            });
        };
        const burst = () => {
            measure();
            burstLeft -= 1;
            if (burstLeft > 0)
                this.burstTimer = setTimeout(burst, 200);
        };
        burst();
        if (this.clockTimer)
            clearInterval(this.clockTimer);
        this.clockTimer = setInterval(measure, 20_000);
    }
    /** Текущее серверное время. Основа всей синхронизации. */
    serverNow() {
        return this.clock.now();
    }
    // ─────────────────────────── действия ───────────────────────────
    sendControl(action, positionMs, rate) {
        if (!this.socket?.connected)
            return;
        this.socket.emit('playback:control', { action, positionMs, rate, epoch: this.state.playback?.epoch, revision: this.state.playback?.revision, commandId: `${this.clientId}:${++this.commandSequence}` });
    }
    sendChat(text) {
        this.socket?.emit('chat:send', { text });
    }
    /** Стикер из телеграма. Что это за стикер, сервер узнаёт сам по номеру. */
    sendSticker(stickerId) {
        this.socket?.emit('chat:sticker', { stickerId });
    }
    setSource(source) {
        this.socket?.emit('source:set', { source });
    }
    reportDuration(durationMs) {
        this.socket?.emit('source:duration', { durationMs, epoch: this.state.playback?.epoch });
    }
    reportBuffering(isBuffering, positionMs) {
        this.socket?.emit('buffering:report', { isBuffering, positionMs });
    }
    /** Что происходит у меня: позиция, своя пауза, реклама. */
    reportPresence(presence) {
        if (!this.socket?.connected)
            return;
        const flags = `${presence.isBuffering}:${presence.localPaused}:${presence.adPlaying}`;
        const now = Date.now();
        if (flags === this.lastPresenceFlags && now - this.lastPresenceAt < 1000)
            return;
        this.lastPresenceAt = now;
        this.lastPresenceFlags = flags;
        this.socket.volatile.emit('presence:report', presence);
    }
    setControlMode(controlMode) {
        this.socket?.emit('room:set_control_mode', { controlMode });
    }
    setSettings(patch) {
        this.socket?.emit('room:set_settings', patch);
    }
    sendReaction(emoji, point) {
        this.socket?.emit('reaction:add', { emoji, point });
    }
    sendStroke(stroke) {
        this.socket?.emit('draw:stroke', stroke);
    }
    undoStroke() {
        this.socket?.emit('draw:undo');
    }
    clearStrokes() {
        this.socket?.emit('draw:clear');
    }
    /**
     * Выбрасывает то, что уже растворилось.
     *
     * Сервер чистит свой буфер сам, но событий об этом не шлёт: рассылать
     * «эта реакция погасла» на каждую было бы дороже, чем посчитать срок
     * на месте. Зовётся тактом отрисовки плеера.
     */
    pruneEphemeral(serverNowMs, trailSeconds) {
        const reactions = this.state.reactions.filter((r) => serverNowMs - r.createdAt < LIMITS.reactionLifetimeMs);
        const strokes = this.state.strokes.filter((s) => serverNowMs - s.createdAt < trailSeconds * 1000);
        if (reactions.length === this.state.reactions.length && strokes.length === this.state.strokes.length) {
            return;
        }
        this.setState({ reactions, strokes });
    }
    updateNickname(nickname) {
        this.nickname = nickname;
        if (this.socket?.connected)
            this.join();
    }
    /** Смена аватарки на лету: перезаходим тем же клиентом, участник тот же. */
    updateAvatar(avatar) {
        this.avatar = avatar;
        if (this.socket?.connected)
            this.join();
    }
    clearError() {
        if (this.state.error)
            this.setState({ error: null });
    }
}
//# sourceMappingURL=connection.js.map